/**
 * ErectionPlanDetail — CR-2026-ERECTOR-E1 (2026-10-13). AISC 207-25 §5.12.6.
 * Edit (DRAFT), PM review, release (site superintendent), revise, distribute.
 * The server enforces every rule; this page only hides actions the user
 * cannot take and shows the server's answer.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert, TextField, Checkbox, FormControlLabel,
  Divider, MenuItem, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  getErectionPlan, updateErectionPlan, pmReviewErectionPlan, returnErectionPlan, releaseErectionPlan,
  reviseErectionPlan, distributeErectionPlan, ERECTION_SAFETY_KEYS, ERECTION_NO_NA, ERECTION_STATUS_LABEL,
  QF_ER_002_DOCX, QF_ER_002_PDF, planOnTemplate, planKeys,
  unaddressedElements, apiMessage,
} from "../../utils/erectionPlansApi";

import SmartTable from "../../components/SmartTable/SmartTable";
export default function ErectionPlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: perms = [] } = loginUser?.decodedToken ?? {};
  const can = {
    edit: hasPermission(perms, PERMISSIONS.EDIT_ERECTION_PLAN),
    review: hasPermission(perms, PERMISSIONS.REVIEW_ERECTION_PLAN),
    release: hasPermission(perms, PERMISSIONS.RELEASE_ERECTION_PLAN),
  };

  const [data, setData] = useState(null);
  const [elements, setElements] = useState({});
  const [cosp, setCosp] = useState({ provided: false, note: "" });
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [dist, setDist] = useState({ names: "", method: "BRIEFING" });

  const load = useCallback(async () => {
    try {
      const d = await getErectionPlan(id);
      setData(d);
      setElements(d.plan.elements || {});
      setCosp({ provided: !!d.plan.cosp_7_10_provided, note: d.plan.cosp_7_10_note || "" });
      setDirty(false);
    } catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Could not load the plan.") }); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const plan = data?.plan;
  const labels = data?.elementLabels || {};
  const editable = plan?.status === "DRAFT" && can.edit;
  const missing = useMemo(() => unaddressedElements(elements), [elements]);

  const setEl = (k, patch) => { setElements((e) => ({ ...e, [k]: { ...(e[k] || {}), ...patch } })); setDirty(true); };

  async function run(fn, okText) {
    setBusy(true); setMsg(null);
    try {
      const out = await fn();
      if (out?.id && Number(out.id) !== Number(id)) { navigate(`/erection-plans/${out.id}`); return true; }
      await load();
      setMsg({ severity: "success", text: out?.reminder || okText });
      return true;
    } catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Not saved.") }); return false; }
    finally { setBusy(false); }
  }

  if (!plan) return <Container sx={{ pt: 3 }}>{msg ? <Alert severity={msg.severity}>{msg.text}</Alert> : "Loading…"}</Container>;

  return (
    <>
      <Helmet><title>Erection Plan Rev {String(plan.revision)} | SFM</title></Helmet>
      <Container maxWidth="lg">
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Button onClick={() => navigate("/erection-plans")}>← Erection Plans</Button>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Erection Plan · Rev {plan.revision}</Typography>
          <Chip label={ERECTION_STATUS_LABEL[plan.status] || plan.status} color={plan.status === "RELEASED" ? "success" : "default"} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {planOnTemplate(elements) ? "QF-ER-002 Rev 0 · OSHA 1926.752(e) · " : ""}AISC 207-25 §5.12.6 · {planKeys(elements).length - missing.length} of {planKeys(elements).length} items addressed
          {plan.revision_reason ? ` · Revision reason: ${plan.revision_reason}` : ""}
        </Typography>
        {plan.status === "SUPERSEDED" && <Alert severity="warning" sx={{ mb: 2 }}>Superseded by plan #{plan.superseded_by_id}. Kept on the record; do not use for erection.</Alert>}
        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }}>{msg.text}</Alert>}

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" variant="outlined" href={QF_ER_002_DOCX} download>Blank form QF-ER-002 (Word)</Button>
          <Button size="small" href={QF_ER_002_PDF} target="_blank" rel="noopener">PDF</Button>
        </Stack>
        <Paper sx={{ p: 2, mb: 2 }}>
          {planKeys(elements).map((k) => {
            const v = elements[k] || {};
            const open = missing.includes(k);
            const safety = ERECTION_SAFETY_KEYS.includes(k);
            const noNa = ERECTION_NO_NA.includes(k);
            return (
              <Stack key={k} spacing={1} sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                {k === ERECTION_SAFETY_KEYS[0] && (
                  <Typography variant="h6" sx={{ pt: 1 }}>Safety plan (QF-ER-002, OSHA 1926 Subpart R)</Typography>
                )}
                <Typography variant="subtitle2" color={open ? "error" : "text.primary"}>
                  {safety ? `S${ERECTION_SAFETY_KEYS.indexOf(k) + 1}.` : `(${k})`} {labels[k]}{noNa ? " · always applies" : ""}
                </Typography>
                <TextField
                  multiline minRows={2} fullWidth size="small" disabled={!editable || v.na}
                  value={v.text || ""} onChange={(e) => setEl(k, { text: e.target.value })}
                  placeholder={v.na ? "Marked not applicable" : "Describe how this job addresses this item"}
                />
                {!noNa && <Stack direction="row" spacing={2} alignItems="center">
                  <FormControlLabel
                    control={<Checkbox size="small" disabled={!editable} checked={!!v.na} onChange={(e) => setEl(k, { na: e.target.checked })} />}
                    label="Not applicable"
                  />
                  {v.na && (
                    <TextField size="small" sx={{ flex: 1 }} disabled={!editable} label="Why not applicable (required)"
                      value={v.na_reason || ""} onChange={(e) => setEl(k, { na_reason: e.target.value })} />
                  )}
                </Stack>}
                {noNa && v.na && <Alert severity="warning">This item always applies and cannot be N/A. Describe it above.</Alert>}
              </Stack>
            );
          })}
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={<Checkbox disabled={!editable} checked={cosp.provided} onChange={(e) => { setCosp((c) => ({ ...c, provided: e.target.checked })); setDirty(true); }} />}
            label="AISC Code of Standard Practice §7.10 information has been provided"
          />
          <TextField fullWidth size="small" disabled={!editable} label="§7.10 note (what was provided, to whom, when)"
            value={cosp.note} onChange={(e) => { setCosp((c) => ({ ...c, note: e.target.value })); setDirty(true); }} />
        </Paper>

        <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
          {editable && (
            <Button variant="contained" disabled={busy || !dirty}
              onClick={() => run(() => updateErectionPlan(id, { elements, cosp_7_10_provided: cosp.provided, cosp_7_10_note: cosp.note }), "Saved.")}>
              Save
            </Button>
          )}
          {plan.status === "DRAFT" && can.review && (
            <Button variant="outlined" disabled={busy || dirty || missing.length > 0 || !cosp.provided}
              onClick={() => run(() => pmReviewErectionPlan(id), "PM review recorded.")}>
              Record PM team review
            </Button>
          )}
          {plan.status === "PM_REVIEWED" && can.release && (
            <Button variant="contained" color="success" disabled={busy}
              onClick={() => run(() => releaseErectionPlan(id), "Released.")}>
              Release (site superintendent)
            </Button>
          )}
          {plan.status === "PM_REVIEWED" && can.review && (
            <Button variant="outlined" color="warning" disabled={busy || !reason.trim()}
              onClick={() => run(() => returnErectionPlan(id, { reason }), "Returned to draft.")}>
              Return to draft
            </Button>
          )}
          {plan.status === "RELEASED" && can.edit && (
            <Button variant="outlined" disabled={busy || !reason.trim()}
              onClick={() => run(() => reviseErectionPlan(id, { reason }), "Revision opened.")}>
              Start a revision
            </Button>
          )}
          {(plan.status === "PM_REVIEWED" || plan.status === "RELEASED") && (can.review || can.edit) && (
            <TextField size="small" label="Reason (return or revision)" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ minWidth: 320 }} />
          )}
        </Stack>

        {plan.status !== "DRAFT" && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Distribution to affected personnel</Typography>
            {plan.status === "RELEASED" && can.edit && (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField size="small" sx={{ flex: 1 }} label="Recipients (comma separated)" value={dist.names}
                  onChange={(e) => setDist((d) => ({ ...d, names: e.target.value }))} />
                <TextField select size="small" value={dist.method} onChange={(e) => setDist((d) => ({ ...d, method: e.target.value }))}>
                  {["BRIEFING", "PRINT", "DEVICE", "EMAIL"].map((m) => <MenuItem key={m} value={m}>{m.toLowerCase()}</MenuItem>)}
                </TextField>
                <Button variant="outlined" disabled={busy || !dist.names.trim()}
                  onClick={() => run(() => distributeErectionPlan(id, {
                    method: dist.method,
                    recipients: dist.names.split(",").map((n) => ({ name: n.trim() })).filter((r) => r.name),
                  }), "Distribution recorded.").then((ok) => { if (ok) setDist((d) => ({ ...d, names: "" })); })}>
                  Record
                </Button>
              </Stack>
            )}
            <SmartTable tableId="erection-plan-distribution" lockedColumns={["Recipient", "When"]} size="small">
              <TableHead><TableRow><TableCell>Recipient</TableCell><TableCell>Method</TableCell><TableCell>When</TableCell></TableRow></TableHead>
              <TableBody>
                {(data.distributions || []).length === 0 && <TableRow><TableCell colSpan={3}>Not yet communicated.</TableCell></TableRow>}
                {(data.distributions || []).map((d) => (
                  <TableRow key={d.id}><TableCell>{d.recipient_name}</TableCell><TableCell>{d.method.toLowerCase()}</TableCell>
                    <TableCell>{new Date(d.distributed_at).toLocaleString()}</TableCell></TableRow>
                ))}
              </TableBody>
            </SmartTable>
          </Paper>
        )}
      </Container>
    </>
  );
}
